export const TAX_REGIMES = {
  lucro_presumido: {
    label: "Lucro Presumido",
    aliquotas: { pis: 0.65, cofins: 3.0, irpj: 1.2, csll: 1.08, iss: 0.39 },
    total: 6.32,
    editable: false,
  },
  ret: {
    label: "RET (Regime Especial de Tributa\u00e7\u00e3o)",
    aliquotas: { pis: 0.37, cofins: 1.71, irpj: 1.26, csll: 0.66 },
    total: 4.0,
    editable: false,
  },
  lucro_real: {
    label: "Lucro Real",
    aliquotas: { pis: 0.65, cofins: 3.0, irpj: 1.2, csll: 1.08, iss: 0.0 },
    editable: false,
  },
  personalizado: {
    label: "Personalizado",
    aliquotas: { pis: 0, cofins: 0, irpj: 0, csll: 0, iss: 0 },
    editable: true,
  },
};

export const DEFAULT_TAX_REGIME = "lucro_presumido";

export const DEFAULT_DESPESAS_ALIQUOTAS = {
  corretagem: 5.0,
  publicidade: 2.0,
  adm_central: 5.0,
};

export const DRE_LINES = [
  { key: "vgv", label: "VGV Bruto", type: "input", indent: 0, bold: true },
  { key: "impostos", label: "(-) Impostos", type: "input", indent: 1 },
  { key: "vgv_liquido", label: "VGV Líquido", type: "subtotal", indent: 0, bold: true, highlight: "emerald" },
  { key: "custo_vendas", label: "CUSTOS TOTAL DAS VENDAS", type: "subtotal", indent: 0, bold: true },
  { key: "custo_construcao", label: "Custo das Unidades Vendidas", type: "input", indent: 1 },
  { key: "provisao_assistencia_tecnica", label: "Provisão de Assistência Técnica", type: "input", indent: 1 },
  { key: "outros_custos", label: "Outros Custos", type: "input", indent: 1, editableColumns: true },
  { key: "juros_financiamento_obra", label: "Juros s/Finactº da Obra", type: "input", indent: 1, editableColumns: true },
  { key: "resultado_bruto", label: "Resultado Bruto", type: "subtotal", indent: 0, bold: true, highlight: "blue" },
  { key: "despesas_vendas", label: "(-) Despesas de Vendas", type: "subtotal", indent: 0, bold: true },
  { key: "corretagem", label: ". Corretagem", type: "input", indent: 1 },
  { key: "publicidade", label: ". Publicidade", type: "input", indent: 1 },
  { key: "lucro_operacional", label: "Lucro Operacional", type: "subtotal", indent: 0, bold: true, highlight: "amber" },
  { key: "despesas", label: "(-) Despesas", type: "subtotal", indent: 0, bold: true },
  { key: "despesas_adm_central", label: "(-) Despesas da Adm. Central", type: "input", indent: 1, editableColumns: true },
  { key: "custos_estruturacao_financeira", label: "(-) Despesas Financeiras", type: "input", indent: 1, editableColumns: true },
  { key: "despesa_tributaria", label: "(-) Despesa Tributária", type: "input", indent: 1, editableColumns: true },
  { key: "imprevistos", label: "(-) Outras Despesas", type: "input", indent: 1, editableColumns: true },
  { key: "receita_financeira", label: "(+) Receita Financeira", type: "input", indent: 1, editableColumns: true },
  { key: "lucro_liquido", label: "Lucro Líquido", type: "subtotal", indent: 0, bold: true, highlight: "purple" },
];

const EDITABLE_IMPORTED_LINE_KEYS = new Set([
  "outros_custos",
  "juros_financiamento_obra",
  "publicidade",
  "despesas_adm_central",
  "custos_estruturacao_financeira",
  "despesa_tributaria",
  "imprevistos",
  "receita_financeira",
]);

export const DRE_COLUMNS = [
  { key: "viabilidade", label: "Viabilidade R$", editable: true },
  { key: "pct_viab", label: "% Viab.", editable: false },
  { key: "apropriado", label: "Realizado", editable: true },
  { key: "a_apropriar", label: "A Apropriar", editable: true },
  { key: "a_contratar", label: "A Contratar", editable: true },
  { key: "a_realizar", label: "A Realizar", editable: false },
  { key: "tendencia", label: "Tendência", editable: false },
  { key: "pct_tend", label: "% Tend.", editable: false },
  { key: "desvio_rs", label: "Desvio R$", editable: false },
  { key: "desvio_pct", label: "Desv. %", editable: false },
];

export function getDefaultTaxAliquotas() {
  return { ...TAX_REGIMES[DEFAULT_TAX_REGIME].aliquotas };
}

export function normalizeTaxRegimeAliquotas(regime, aliquotas = {}) {
  if (regime !== "personalizado" && TAX_REGIMES[regime]) {
    return { ...TAX_REGIMES[regime].aliquotas };
  }

  return { ...aliquotas };
}

export function getDefaultExpenseAliquotas() {
  return { ...DEFAULT_DESPESAS_ALIQUOTAS };
}

export function getTotalAliquota(aliquotas) {
  return Object.values(aliquotas || {}).reduce((total, value) => total + (Number(value) || 0), 0);
}

export function getEmptyLineData() {
  const lineData = {};

  DRE_LINES.forEach((line) => {
    if (line.type === "input") {
      lineData[line.key] = { viabilidade: 0 };
    }
  });

  return lineData;
}

export function isEditableImportedColumn(lineKey, columnKey) {
  return (
    EDITABLE_IMPORTED_LINE_KEYS.has(lineKey) &&
    ["apropriado", "a_apropriar", "a_contratar"].includes(columnKey)
  );
}

export function calcViabilidade(lineData) {
  const getValue = (key) => Number(lineData[key]?.viabilidade || 0);
  const result = {};

  result.vgv = getValue("vgv");
  result.impostos = getValue("impostos");
  result.vgv_liquido = result.vgv - result.impostos;

  result.custo_construcao = getValue("custo_construcao");
  result.provisao_assistencia_tecnica = getValue("provisao_assistencia_tecnica");
  result.outros_custos = getValue("outros_custos");
  result.juros_financiamento_obra = getValue("juros_financiamento_obra");
  result.custo_vendas =
    result.custo_construcao +
    result.provisao_assistencia_tecnica +
    result.outros_custos +
    result.juros_financiamento_obra;

  result.resultado_bruto = result.vgv_liquido - result.custo_vendas;

  result.corretagem = getValue("corretagem");
  result.publicidade = getValue("publicidade");
  result.despesas_vendas = result.corretagem + result.publicidade;

  result.lucro_operacional = result.resultado_bruto - result.despesas_vendas;

  result.despesas_adm_central = getValue("despesas_adm_central");
  result.custos_estruturacao_financeira = getValue("custos_estruturacao_financeira");
  result.despesa_tributaria = getValue("despesa_tributaria");
  result.imprevistos = getValue("imprevistos");
  result.receita_financeira = getValue("receita_financeira");
  result.despesas =
    result.despesas_adm_central +
    result.custos_estruturacao_financeira +
    result.despesa_tributaria +
    result.imprevistos -
    result.receita_financeira;

  result.lucro_liquido = result.lucro_operacional - result.despesas;

  return result;
}

export function calcFullDre(lineData, _impostoPercentual, dadosCarregados = {}) {
  const viabilidade = calcViabilidade(lineData);
  const vgvLiquidoBase = viabilidade.vgv_liquido || 1;
  const vgvBrutoBase = viabilidade.vgv || 1;

  const rows = {};
  const getColValue = (key, col) => {
    if (isEditableImportedColumn(key, col)) {
      return Object.prototype.hasOwnProperty.call(lineData[key] || {}, col)
        ? Number(lineData[key]?.[col] || 0)
        : 0;
    }
    return Number(dadosCarregados[key]?.[col] || 0);
  };

  DRE_LINES.forEach((line) => {
    if (line.type === "input") {
      rows[line.key] = {
        viabilidade: viabilidade[line.key] || 0,
        apropriado: getColValue(line.key, "apropriado"),
        a_apropriar: getColValue(line.key, "a_apropriar"),
        a_contratar: getColValue(line.key, "a_contratar"),
      };
    }
  });

  const sumByKeys = (keys, column) =>
    keys.reduce((total, key) => total + Number(rows[key]?.[column] || 0), 0);

  const custoVendasKeys = ["custo_construcao", "provisao_assistencia_tecnica", "outros_custos", "juros_financiamento_obra"];
  rows.custo_vendas = {
    viabilidade: viabilidade.custo_vendas || 0,
    apropriado: sumByKeys(custoVendasKeys, "apropriado"),
    a_apropriar: sumByKeys(custoVendasKeys, "a_apropriar"),
    a_contratar: sumByKeys(custoVendasKeys, "a_contratar"),
  };

  rows.vgv_liquido = {
    viabilidade: viabilidade.vgv_liquido || 0,
    apropriado: Number(rows.vgv?.apropriado || 0) - Number(rows.impostos?.apropriado || 0),
    a_apropriar: Number(rows.vgv?.a_apropriar || 0) - Number(rows.impostos?.a_apropriar || 0),
    a_contratar: Number(rows.vgv?.a_contratar || 0) - Number(rows.impostos?.a_contratar || 0),
  };

  rows.resultado_bruto = {
    viabilidade: viabilidade.resultado_bruto || 0,
    apropriado: Number(rows.vgv_liquido?.apropriado || 0) - Number(rows.custo_vendas?.apropriado || 0),
    a_apropriar: Number(rows.vgv_liquido?.a_apropriar || 0) - Number(rows.custo_vendas?.a_apropriar || 0),
    a_contratar: Number(rows.vgv_liquido?.a_contratar || 0) - Number(rows.custo_vendas?.a_contratar || 0),
  };

  const despesasVendasKeys = ["corretagem", "publicidade"];
  rows.despesas_vendas = {
    viabilidade: viabilidade.despesas_vendas || 0,
    apropriado: sumByKeys(despesasVendasKeys, "apropriado"),
    a_apropriar: sumByKeys(despesasVendasKeys, "a_apropriar"),
    a_contratar: sumByKeys(despesasVendasKeys, "a_contratar"),
  };

  rows.lucro_operacional = {
    viabilidade: viabilidade.lucro_operacional || 0,
    apropriado: Number(rows.resultado_bruto?.apropriado || 0) - Number(rows.despesas_vendas?.apropriado || 0),
    a_apropriar: Number(rows.resultado_bruto?.a_apropriar || 0) - Number(rows.despesas_vendas?.a_apropriar || 0),
    a_contratar: Number(rows.resultado_bruto?.a_contratar || 0) - Number(rows.despesas_vendas?.a_contratar || 0),
  };

  const despesasKeys = [
    "despesas_adm_central",
    "custos_estruturacao_financeira",
    "despesa_tributaria",
    "imprevistos",
  ];
  rows.despesas = {
    viabilidade: viabilidade.despesas || 0,
    apropriado: sumByKeys(despesasKeys, "apropriado") - Number(rows.receita_financeira?.apropriado || 0),
    a_apropriar: sumByKeys(despesasKeys, "a_apropriar") - Number(rows.receita_financeira?.a_apropriar || 0),
    a_contratar: sumByKeys(despesasKeys, "a_contratar") - Number(rows.receita_financeira?.a_contratar || 0),
  };

  rows.lucro_liquido = {
    viabilidade: viabilidade.lucro_liquido || 0,
    apropriado: Number(rows.lucro_operacional?.apropriado || 0) - Number(rows.despesas?.apropriado || 0),
    a_apropriar: Number(rows.lucro_operacional?.a_apropriar || 0) - Number(rows.despesas?.a_apropriar || 0),
    a_contratar: Number(rows.lucro_operacional?.a_contratar || 0) - Number(rows.despesas?.a_contratar || 0),
  };

  const vgvLiquidoTendencia =
    Number(rows.vgv_liquido?.apropriado || 0) +
    Number(rows.vgv_liquido?.a_apropriar || 0) +
    Number(rows.vgv_liquido?.a_contratar || 0) ||
    1;

  const finalRows = {};

  DRE_LINES.forEach((line) => {
    const row = rows[line.key] || {
      viabilidade: 0,
      apropriado: 0,
      a_apropriar: 0,
      a_contratar: 0,
    };

    const aRealizar = row.a_apropriar + row.a_contratar;
    const tendencia = row.apropriado + aRealizar;
    const desvioRs = tendencia - row.viabilidade;

    finalRows[line.key] = {
      viabilidade: row.viabilidade,
      pct_viab: line.key === "impostos"
        ? (row.viabilidade / vgvBrutoBase) * 100
        : (row.viabilidade / vgvLiquidoBase) * 100,
      apropriado: row.apropriado,
      a_apropriar: row.a_apropriar,
      a_contratar: row.a_contratar,
      a_realizar: aRealizar,
      tendencia,
      pct_tend: vgvLiquidoTendencia ? (tendencia / vgvLiquidoTendencia) * 100 : 0,
      desvio_rs: desvioRs,
      desvio_pct: row.viabilidade ? (desvioRs / row.viabilidade) * 100 : 0,
    };
  });

  return finalRows;
}
